import { Controller } from "@hotwired/stimulus"
import { useTurboCache } from "../utils/use_turbo_cache"

/**
 * Dropdown Controller
 *
 * Initializes Bootstrap dropdowns with proper Turbo Drive support.
 * Handles cleanup of Popper.js positioning attributes before caching.
 *
 * NOTE: This controller expects window.bootstrap.Dropdown to be available.
 * On WWW, Bootstrap is loaded in the main bundle via client/js/www/setup/bootstrap.js.
 * For pages that don't load the full bundle, Bootstrap's data-api will still work
 * as a fallback.
 *
 * @controller dropdown
 *
 * @example Navigation with dropdowns
 * <nav data-controller="dropdown">
 *   <div class="dropdown">
 *     <button data-bs-toggle="dropdown">Menu</button>
 *     <ul class="dropdown-menu">...</ul>
 *   </div>
 * </nav>
 */
export default class extends Controller {
  static selector = '[data-bs-toggle="dropdown"], [data-toggle="dropdown"]'

  connect() {
    this.initializeDropdowns()
    useTurboCache(this, { cleanup: this.cleanupDropdowns.bind(this) })
  }

  initializeDropdowns() {
    if (!window.bootstrap?.Dropdown) return

    this.element.querySelectorAll(this.constructor.selector).forEach(triggerEl => {
      // Clean up any stale Popper state from cache restoration
      this.cleanDropdownElement(triggerEl)

      // Clean up any stale instance and create fresh one
      const existingInstance = window.bootstrap.Dropdown.getInstance(triggerEl)
      if (existingInstance) {
        existingInstance.dispose()
      }

      new window.bootstrap.Dropdown(triggerEl)
    })
  }

  cleanupDropdowns() {
    if (!window.bootstrap?.Dropdown) return

    this.element.querySelectorAll(this.constructor.selector).forEach(triggerEl => {
      // Dispose Bootstrap instance
      const instance = window.bootstrap.Dropdown.getInstance(triggerEl)
      if (instance) {
        instance.hide()
        instance.dispose()
      }

      // Clean up DOM state (Popper attributes)
      this.cleanDropdownElement(triggerEl)
    })
  }

  // Clean up dropdown element state (removes Popper positioning, show classes)
  cleanDropdownElement(triggerEl) {
    triggerEl.classList.remove('show')
    triggerEl.setAttribute('aria-expanded', 'false')

    // Find the dropdown menu
    const menuSelector = triggerEl.getAttribute('aria-controls') ||
                         triggerEl.getAttribute('data-bs-target')
    let menu = menuSelector ? document.getElementById(menuSelector) : null

    if (!menu) {
      menu = triggerEl.nextElementSibling?.classList.contains('dropdown-menu')
             ? triggerEl.nextElementSibling
             : triggerEl.parentElement?.querySelector('.dropdown-menu')
    }

    if (menu) {
      menu.classList.remove('show')
      menu.removeAttribute('style')
      menu.removeAttribute('data-popper-placement')
    }
  }
}
